
import React, { useState, useRef } from 'react';
import { 
  PhotoIcon, 
  CloudArrowUpIcon, 
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = 'https://fciveria.ge';
  return `${baseUrl}${endpoint}`;
};

const ImageUpload = ({ 
  value = '', 
  onChange, 
  category = 'photos',
  label = 'Image',
  accept = 'image/*',
  maxSize = 50, // MB
  required = false,
  preview = true,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  const handleFileSelect = async (file) => {
    setError('');
    
    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      setError(`File size too large. Maximum size is ${maxSize}MB.`);
      return;
    }

    await uploadFile(file);
  };

  const testUploadEndpoint = async () => {
    try {
      const testUrl = getApiUrl('/api/upload/test');
      console.log('Testing upload endpoint:', testUrl);
      
      const response = await fetch(testUrl);
      const result = await response.json();
      console.log('Upload endpoint test result:', result);
      return result;
    } catch (error) {
      console.error('Upload endpoint test failed:', error);
      return null;
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setError('');

    try {
      // Test upload endpoint first
      const testResult = await testUploadEndpoint();
      if (!testResult) {
        setError('Upload service is not accessible. Please try again later.');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      // Debug logging
      console.log('Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        category: category
      });
      
      // Debug FormData contents
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      // Get token using the same method as apiService
      const storedUser = localStorage.getItem('fciveria_admin_user');
      let token = null;
      
      try {
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          token = userData.token;
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('fciveria_admin_user');
      }
      
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      
      // Debug token info
      console.log('Token debug:', {
        hasStoredUser: !!storedUser,
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token'
      });
      
      console.log('Upload URL:', getApiUrl('/api/upload/image'));
      console.log('Token present:', !!token);
      
      const uploadUrl = getApiUrl('/api/upload/image');
      console.log('Upload URL:', uploadUrl);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Don't set Content-Type for multipart/form-data - let browser set it with boundary
        },
        body: formData
      });

      console.log('Upload response status:', response.status);
      console.log('Upload response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload successful:', result);
        onChange(result.url);
      } else {
        // Try to get error details
        let errorData;
        try {
          const text = await response.text();
          console.log('Error response text:', text);
          errorData = JSON.parse(text);
        } catch (parseError) {
          console.log('Could not parse error response as JSON');
          errorData = { error: `Upload failed with status ${response.status}: ${response.statusText}` };
        }
        
        console.error('Upload failed:', errorData);
        
        // Special handling for "No file provided" error
        if (errorData.error === 'No file provided') {
          setError('Server could not process the file. This might be a server configuration issue. Please try again or contact support.');
        } else if (response.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else {
          setError(errorData.error || `Upload failed with status ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    if (file) {
      handleFileSelect(file);
    } else {
      console.log('No file selected');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const clearImage = () => {
    onChange('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 font-georgian">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragOver 
            ? 'border-blue-400 bg-blue-50' 
            : uploading 
            ? 'border-gray-300 bg-gray-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600 font-georgian">იტვირთება...</p>
          </div>
        ) : (
          <div className="text-center">
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-medium text-blue-600 hover:text-blue-500 font-georgian"
              >
                ფაილის ატვირთვა
              </button>
              <p className="text-sm text-gray-500 font-georgian">ან გადაიტანეთ აქ</p>
            </div>
            <p className="text-xs text-gray-500 font-georgian mt-1">
              PNG, JPG, GIF, WebP მაქს. {maxSize}MB
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-md">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
          <p className="text-sm text-red-600 font-georgian">{error}</p>
        </div>
      )}

      {/* URL Input Alternative */}
      <div className="relative">
        <label className="block text-xs text-gray-500 font-georgian mb-1">
          ან შეიყვანეთ URL
        </label>
        <input
          type="url"
          value={value}
          onChange={handleUrlChange}
          placeholder="https://example.com/image.jpg"
          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
      </div>

      {/* Debug Test Button */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => {
            console.log('File input ref:', fileInputRef.current);
            console.log('File input files:', fileInputRef.current?.files);
            if (fileInputRef.current?.files?.length > 0) {
              console.log('First file:', fileInputRef.current.files[0]);
            }
          }}
          className="text-xs text-gray-500 hover:text-gray-700 font-georgian"
        >
          Debug File Input
        </button>
      </div>

      {/* Preview */}
      {preview && value && (
        <div className="relative">
          <label className="block text-xs text-gray-500 font-georgian mb-2">
            გადახედვა
          </label>
          <div className="relative inline-block">
            <img
              src={value}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border shadow-sm"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              style={{display: 'none'}} 
              className="w-32 h-32 bg-gray-100 rounded-lg border shadow-sm flex items-center justify-center"
            >
              <PhotoIcon className="h-8 w-8 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={clearImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
